import { useEffect, useRef, useState } from "react";
import { Portrait } from "../portrait/Portrait";
import type { QueueItem } from "../types";
import { Parts, PersonRef } from "./PersonRef";
import { TonePicker } from "./Reply";
import type { Tone } from "../email";

/** People waiting outside the door, in arrival order (19 §2.3). The routing engine made visible. */
export function Queue({ items, busy, onAnswer }: { items: QueueItem[]; busy: boolean; onAnswer: (instanceId: string, optionId: string, tone?: Tone) => void }) {
  const [toneFor, setToneFor] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const selected = items.find((q) => q.instanceId === open) ?? null;
  const title = useRef<HTMLHeadingElement>(null);
  const detail = useRef<HTMLDivElement>(null);
  const hadTone = useRef(false);
  // Leaving the tone step returns focus to the replies it came from (WCAG 2.4.3).
  useEffect(() => {
    if (hadTone.current && !toneFor) detail.current?.querySelector<HTMLButtonElement>(".queue__options button")?.focus();
    hadTone.current = toneFor !== null;
  }, [toneFor]);
  /** Once answered, the request leaves the queue: keep focus in the queue rather than losing it. */
  const answered = () => requestAnimationFrame(() => title.current?.focus({ preventScroll: true }));
  return (
    <aside className="queue" aria-labelledby="queue-title">
      <h2 id="queue-title" className="panel-title" ref={title} tabIndex={-1}>Outside your door</h2>
      {items.length === 0 ? (
        <p className="queue__empty">The corridor is quiet.</p>
      ) : (
        <>
          <p className="queue__count">{items.length === 1 ? "1 person waiting" : `${items.length} people waiting`}</p>
          <ul className="queue__list">
            {items.map((q) => {
              const name = q.who?.name ?? "Someone";
              return (
                <li key={q.instanceId} className="queue__member">
                  <button type="button" className="queue__btn" aria-expanded={open === q.instanceId}
                    aria-controls={open === q.instanceId ? "queue-detail" : undefined}
                    onClick={() => { setToneFor(null); setOpen(open === q.instanceId ? null : q.instanceId); }}>
                    {q.who ? (
                      <Portrait portraitId={q.who.portraitId} seed={q.who.id} name={q.who.name} grade={q.who.grade} size={44} decorative />
                    ) : <span className="queue__nobody" aria-hidden="true">✉</span>}
                    <span className="queue__text">
                      <span className="queue__name">{q.who ? <PersonRef personId={q.who.id} person={q.who} passive>{name}</PersonRef> : name}</span>
                      <span className="queue__reason">{q.title}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          {selected && (
            <div className="queue__detail" id="queue-detail" ref={detail} role="group" aria-label={`${selected.who?.name ?? "Request"}: ${selected.title}`}>
              {selected.who && <p className="queue__from"><PersonRef personId={selected.who.id} person={selected.who}>{selected.who.name}</PersonRef></p>}
              <p className="queue__quote">“<Parts parts={selected.event.textParts} text={selected.event.text} />”</p>
              {selected.canWait ? (
                <>
                  <p className="muted">This can wait. If you don't get to it by tomorrow, it will be handled the usual way.</p>
                  {toneFor && selected.event.options.find((o) => o.id === toneFor) ? (
                    <TonePicker option={selected.event.options.find((o) => o.id === toneFor)!} busy={busy}
                      onBack={() => setToneFor(null)}
                      onSend={(tone) => { const id = toneFor; hadTone.current = false; setToneFor(null); setOpen(null); answered(); onAnswer(selected.instanceId, id, tone); }} />
                  ) : (
                    <ul className="queue__options">
                      {selected.event.options.map((o) => (
                        <li key={o.id}>
                          <button type="button" className="btn btn--option" disabled={busy}
                            onClick={() => {
                              // Written replies need a tone first (21 §4b).
                              if (o.tones?.length) { setToneFor(o.id); return; }
                              setOpen(null); answered(); onAnswer(selected.instanceId, o.id);
                            }}><Parts parts={o.textParts} text={o.text} passive /></button>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ) : (
                <p className="muted">{selected.who?.name ?? "They"} will see you after the current conversation.</p>
              )}
            </div>
          )}
        </>
      )}
    </aside>
  );
}
